import {Component,Input,OnInit} from '@angular/core';
import {FilterValue,ListView,TypeComparaison,TypeFilter} from "@domain/common/list-view";
import {UserDto} from "@domain/user/user-dto";
import {ResponsableItemComponent} from "@components/admin/entreprise/analytique/enveloppes/enveloppe-details/enveloppe-responsable/responsable-item/responsable-item.component";
import {TranslateService} from "@ngx-translate/core";
import {Sorting} from "@domain/common/list-view/sorting";
import {EnveloppeDetailService} from "@components/admin/entreprise/analytique/enveloppes/enveloppe-details/enveloppe-detail.service";

/**
 * Composant d'affichage pour la liste des responsables budgétaires des enveloppes
 *
 * @author Lionel Gimenez
 * @date 04/12/2024
 */
@Component({
    host: {'data-test-id': 'enveloppe-responsable'},
    selector: 'enveloppe-responsable',
    templateUrl: './enveloppe-responsable.component.html'
})
export class EnveloppeResponsableComponent implements OnInit {
    /** Id de l'enveloppe */
    @Input() idEnveloppe: number;

    /** Liste des responsables */
    listeResponsable: ListView<UserDto,ResponsableItemComponent>;

    /**
     * Constructeur
     */
    constructor(
        private translateService: TranslateService,
        private enveloppeDetailService: EnveloppeDetailService
    ) {
    }

    /**
     * Hook initialisation
     */
    ngOnInit(): void {
        //Initialisation de la liste
        this.listeResponsable = new ListView<UserDto,ResponsableItemComponent>({
            uri: `/controller/Budget/listeResponsableBudgetaireEnveloppe/${this.idEnveloppe}`,
            title: this.translateService.instant('admin.entreprise.analytique.enveloppes.responsable.title'),
            component: ResponsableItemComponent,
            isFilter: true,
            defaultOrder: 'user.nom',
            listeFilters: [
                {
                    title: this.translateService.instant('filter.prenom'),
                    clef: 'user.prenom',
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                    isDefault: true
                },{
                    title: this.translateService.instant('filter.nom'),
                    clef: 'user.nom',
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                    isDefault: true
                },{
                    title: this.translateService.instant('filter.matricule'),
                    clef: 'user.matricule',
                    typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                    isDefault: true
                },{
                    title: this.translateService.instant('admin.entreprise.analytique.enveloppes.responsable.actif'),
                    clef: 'user.isActif',
                    type: TypeFilter[TypeFilter.BOOLEAN],
                    typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                    isDefault: false,
                    onloadValue: 'true'
                },{
                    title: this.translateService.instant('admin.entreprise.analytique.enveloppes.responsable.role'),
                    clef: 'role.idRole',
                    typeComparaison: TypeComparaison[TypeComparaison.IN],
                    multiple: true,
                    listeValues: [],
                    isDefault: false
                }
            ]
        });

        //Demande de récupération de la liste des rôles de responsable budgétaire possibles pour cette enveloppe
        this.enveloppeDetailService.listeRolesResponsablesBudgetaire(this.idEnveloppe).subscribe(roles => {
            //Set pour assurer l'unicité des rôles dans la liste
            const setRoles: Set<number> = new Set();

            //Liste des valeurs possibles
            const filterValues: FilterValue[] = [];

            //Remplissage de la liste des valeurs possibles exempte de doublons
            roles?.forEach(r => !setRoles.has(r.idRole) && filterValues.push(<FilterValue>{ code: r.idRole.toString(), libelle: r.libelle }) && setRoles.add(r.idRole));

            //On récupère le filtre lié aux rôles
            const roleFiltre = this.listeResponsable.listeFilters.find(f => f.clef === 'role.idRole');

            //Peuplement des valeurs du filtre
            roleFiltre.listeValues = filterValues;
        });

        //Définition des colonnes de tri
        this.listeResponsable.columns = [
            {key: 'user.prenom',title: 'filter.prenom'},
            {key: 'user.nom',title: 'filter.nom'},
            {key: 'user.matricule',title: 'filter.matricule'},
        ];

        //Ajout du tri de la liste selon l'ordre voulu
        this.listeResponsable.sorting = new Sorting(this.listeResponsable.columns,'user.nom');

        //Persistence des filtres
        this.listeResponsable.isPersistFilters = true;
        this.listeResponsable.className = 'EnveloppeResponsableComponent';
    }
}